import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import type { FormEvent } from 'react';
import {
  getAllUsers, createUser, updateUser, deactivateUser, getUserBranches, setUserBranches,
} from '../api';
import { getSpecialties } from '../api';
import { getAllRoles, createRole, updateRole, deleteRole } from '../api';
import type { AdminUser, Specialty, Role, UserBranchGrant } from '../types';
import AdminTabs from '../AdminTabs';
import { useAdminSubTab } from '../adminRoute';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { FormField } from '@/components/ui/FormField';
import { Input, NativeSelect } from '@/components/ui/Input';
import { RowActionsMenu } from '@/components/ui/RowActionsMenu';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { toast } from '@/hooks/useToast';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import { Switch } from '@/components/ui/Switch';
import { useAuth } from '@/hooks/useAuth';
import { resetUserTwoStep } from '@/features/two-step/api';
import { Dash, SettingsToolbar, filterByStatus, matchesSearch, type StatusFilter } from '../shared/SettingsToolbar';

const SINGLETON_ROLES = ['CEO', 'CFO'];
const MIN_ONE_ROLES = ['Admin', 'Executive Director', 'CEO', 'CFO'];
// See the signatoryRoles computation in UsersTab below for why this drives
// the Registration control rather than a role-name check.
const SIGNATORY_MODULES = ['ip:assessment', 'ip:progress-note'];
const EMPTY_FORM = { username: '', password: '', full_name: '', role: 'Receptionist', specialty_id: '' };

export default function UsersRolesTab() {
  const tabs = useAdminSubTab('users');
  const subTab = tabs.active;
  return (
    <div>
      <AdminTabs state={tabs} />
      {subTab === 'users' && <UsersTab />}
      {subTab === 'roles' && <RolesTab />}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [signatoryRoles, setSignatoryRoles] = useState<Set<string>>(new Set());
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [editing, setEditing] = useState<AdminUser | 'new' | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<AdminUser | null>(null);
  const [pwUser, setPwUser] = useState<AdminUser | null>(null);
  const [regUser, setRegUser] = useState<AdminUser | null>(null);
  const [branchUser, setBranchUser] = useState<AdminUser | null>(null);
  const [multiBranch, setMultiBranch] = useState(false);
  const { user: me } = useAuth();
  const [resetTwoStep, setResetTwoStep] = useState<AdminUser | null>(null);
  const [resetPending, setResetPending] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const load = () => getAllUsers().then((u) => { setUsers(u); setLoadError(''); }).catch((e) => setLoadError(e.message));
  useEffect(() => { load(); }, []);
  useEffect(() => { getSpecialties().then(setSpecialties).catch(() => {}); }, []);
  useEffect(() => {
    getAllRoles().then((rs) => {
      setRoles(rs.map((r) => r.name));
      // Scope the Registration control to roles that actually hold a module
      // requireSignatory() (backend/routes/ip/guards.js) gates a sign route
      // on — today ip:assessment/ip:progress-note (Doctor, Admin). Driven by
      // the role's live grants, not a hardcoded role name: a custom role
      // granted one of these shows the control, and Doctor itself would stop
      // showing it if that grant were ever removed. Keep SIGNATORY_MODULES in
      // step with docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md §9.2 as later
      // phases add more (ip:nursing-assessment, ip:discharge-summary, ...).
      setSignatoryRoles(new Set(rs.filter((r) => (r.modules || []).some((m) => SIGNATORY_MODULES.includes(m))).map((r) => r.name)));
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!users || !users.length) return;
    getUserBranches(users[0]!.id).then(({ available }) => setMultiBranch(available.length > 1)).catch(() => {});
  }, [users]);

  const activeCountOf = (role: string) => (users || []).filter((u) => u.role === role && u.active).length;
  const isSoleHolder = (user: AdminUser) => MIN_ONE_ROLES.includes(user.role) && user.active && activeCountOf(user.role) <= 1;
  const specialtyName = (id: number | null) => specialties.find((s) => s.id === id)?.name;

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast({ title: ok, variant: 'success' }); await load(); }
    catch (e: any) { toast({ title: 'Not saved', description: e.message, variant: 'danger' }); }
  };

  const rows = filterByStatus(users ?? [], status)
    .filter((u) => matchesSearch(search, u.full_name, u.username, u.role, specialtyName(u.specialty_id)));

  const columns: DataTableColumn<AdminUser>[] = [
    { key: 'name', header: 'Name', sortable: true, sortValue: (u) => u.full_name,
      render: (u) => (
        <span className="flex flex-col">
          <span className="font-semibold">{u.full_name}</span>
          <span className="font-mono text-[11.5px] text-muted-foreground">{u.username}</span>
        </span>
      ) },
    { key: 'role', header: 'Role', widthClassName: 'w-[180px]', sortable: true, sortValue: (u) => u.role, render: (u) => u.role },
    { key: 'specialty', header: 'Specialty', widthClassName: 'w-[170px]', render: (u) => (u.role === 'Doctor' && specialtyName(u.specialty_id)) || <Dash /> },
    { key: 'status', header: 'Status', widthClassName: 'w-[180px]',
      render: (u) => (
        <span className="flex flex-wrap gap-1">
          <Badge variant={u.active ? 'success' : 'neutral'}>{u.active ? 'Active' : 'Deactivated'}</Badge>
          {/* Can't sign an IP assessment or progress note until this is filled in. */}
          {u.active && signatoryRoles.has(u.role) && !u.registration_no && <Badge variant="warn">No reg. no.</Badge>}
          {u.mfa_enabled && <Badge variant="info">Two-step</Badge>}
        </span>
      ) },
    { key: 'created', header: 'Created', widthClassName: 'w-[110px]', sortable: true, sortValue: (u) => u.created_at,
      className: 'text-ink-muted', render: (u) => new Date(u.created_at).toLocaleDateString('en-IN') },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', widthClassName: 'w-[56px]',
      render: (u) => (
        <RowActionsMenu
          label={`Actions for ${u.full_name}`}
          actions={[
            { label: 'Edit role & details', onSelect: () => setEditing(u) },
            { label: 'Reset password', onSelect: () => setPwUser(u) },
            ...(signatoryRoles.has(u.role) ? [{ label: 'Registration number', hint: u.registration_no ? undefined : 'Needed before this user can sign IP notes', onSelect: () => setRegUser(u) }] : []),
            ...(multiBranch ? [{ label: 'Branch access', onSelect: () => setBranchUser(u) }] : []),
            ...(u.mfa_enabled ? [{
              label: 'Reset two-step login…', danger: true, disabled: u.id === me?.id,
              hint: u.id === me?.id ? 'Change your own from your profile menu' : 'For a lost or replaced phone',
              onSelect: () => { setResetError(null); setResetTwoStep(u); },
            }] : []),
            u.active
              ? { label: 'Deactivate…', danger: true, disabled: isSoleHolder(u), hint: isSoleHolder(u) ? `The only active ${u.role} — at least one is required` : undefined, onSelect: () => setConfirmDeactivate(u) }
              : { label: 'Reactivate', onSelect: () => act(() => updateUser(u.id, { active: true }), `${u.full_name} reactivated`) },
          ]}
        />
      ) },
  ];

  return (
    <div>
      <SettingsToolbar
        search={search}
        onSearch={setSearch}
        placeholder="Search name, username or role"
        status={status}
        onStatus={setStatus}
        count={users ? `${rows.length} user${rows.length === 1 ? '' : 's'}` : undefined}
        action={<Button onClick={() => setEditing('new')}><Plus className="h-4 w-4" /> Add user</Button>}
      />
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(u) => u.id}
        isLoading={!users && !loadError}
        isError={!!loadError}
        errorMessage={loadError}
        onRetry={load}
        initialSort={{ key: 'name', direction: 'asc' }}
        emptyLabel={search ? 'No users match' : status === 'inactive' ? 'No deactivated users' : 'No users yet'}
      />

      <UserSheet
        user={editing}
        roles={roles}
        specialties={specialties}
        activeCountOf={activeCountOf}
        isSoleHolder={isSoleHolder}
        onClose={() => setEditing(null)}
        onSaved={async (msg) => { setEditing(null); toast({ title: msg, variant: 'success' }); await load(); }}
      />

      <ConfirmDialog
        open={!!confirmDeactivate}
        onOpenChange={(o) => { if (!o) setConfirmDeactivate(null); }}
        title={`Deactivate ${confirmDeactivate?.full_name ?? ''}?`}
        description="They can no longer sign in. Everything they recorded stays attributed to them, and you can reactivate the account later."
        confirmLabel="Deactivate"
        onConfirm={() => { const u = confirmDeactivate; if (u) act(() => deactivateUser(u.id), `${u.full_name} deactivated`); }}
      />

      <TextPromptDialog
        open={!!resetTwoStep}
        title={`Reset two-step login for ${resetTwoStep?.full_name ?? ''}?`}
        description="Two-step login is turned off for this account and they are signed out everywhere. If their role requires two-step, they set it up again at their next sign-in. Do this only after checking it is really them asking."
        label="Reason"
        placeholder="e.g. Lost phone — identity checked in person"
        confirmLabel="Reset two-step login"
        danger
        pending={resetPending}
        error={resetError}
        onClose={() => setResetTwoStep(null)}
        onConfirm={async (reason) => {
          const u = resetTwoStep; if (!u) return;
          setResetPending(true); setResetError(null);
          try {
            await resetUserTwoStep(u.id, reason);
            setResetTwoStep(null);
            toast({ title: `Two-step login reset for ${u.full_name}`, variant: 'success' });
            await load();
          } catch (e: any) { setResetError(e.message); }
          finally { setResetPending(false); }
        }}
      />

      {pwUser && <ChangePasswordModal user={pwUser} onClose={() => setPwUser(null)} />}
      {regUser && <RegistrationModal user={regUser} onClose={() => setRegUser(null)} onSaved={load} />}
      {branchUser && <BranchesModal user={branchUser} onClose={() => setBranchUser(null)} />}
    </div>
  );
}

/**
 * Add a user, or change an existing user's name, role and specialty. Role
 * changes used to be a dropdown in every table row, saved on change — one
 * mis-click re-roled someone. They now go through Save here.
 */
function UserSheet({ user, roles, specialties, activeCountOf, isSoleHolder, onClose, onSaved }: {
  user: AdminUser | 'new' | null;
  roles: string[];
  specialties: Specialty[];
  activeCountOf: (role: string) => number;
  isSoleHolder: (u: AdminUser) => boolean;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const isNew = user === 'new';
  const existing = user && user !== 'new' ? user : null;
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setError(''); setTouched(false);
    setForm(user === 'new' ? EMPTY_FORM : { username: user.username, password: '', full_name: user.full_name, role: user.role, specialty_id: user.specialty_id ? String(user.specialty_id) : '' });
  }, [user]);

  const f = (k: keyof typeof EMPTY_FORM) => (e: { target: { value: string } }) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const roleLocked = !!existing && isSoleHolder(existing);
  const missing = !form.full_name.trim() || (isNew && (!form.username.trim() || form.password.length < 8));

  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    setTouched(true);
    if (missing) return;
    setSaving(true); setError('');
    const specialty_id = form.role === 'Doctor' ? (Number(form.specialty_id) || null) : null;
    try {
      if (isNew) {
        await createUser({ ...form, full_name: form.full_name.trim(), username: form.username.trim(), specialty_id });
        onSaved(`${form.full_name.trim()} added`);
      } else if (existing) {
        await updateUser(existing.id, { full_name: form.full_name.trim(), role: form.role, specialty_id });
        onSaved(`${form.full_name.trim()} saved`);
      }
    } catch (err: any) { setError(err.message); }
    setSaving(false);
  };

  return (
    <Sheet open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent>
        <form onSubmit={save} className="flex min-h-0 flex-1 flex-col">
          <SheetHeader>
            <SheetTitle>{isNew ? 'Add user' : `Edit ${existing?.full_name ?? 'user'}`}</SheetTitle>
            <SheetDescription>{isNew ? 'A staff account. The role decides which modules they can open.' : 'Password, registration number and branch access are in the row menu.'}</SheetDescription>
          </SheetHeader>
          <SheetBody>
            {error && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">{error}</div>}
            <FormField label="Full name" required error={touched && !form.full_name.trim() ? 'Enter their name' : undefined}>
              <Input value={form.full_name} onChange={f('full_name')} autoFocus />
            </FormField>
            <FormField label="Username" required={isNew} error={touched && isNew && !form.username.trim() ? 'Enter a username' : undefined}
              hint={isNew ? 'Used to sign in. Can’t be changed later.' : 'Can’t be changed.'}>
              <Input value={form.username} onChange={f('username')} disabled={!isNew} autoComplete="off" className="font-mono" />
            </FormField>
            {isNew && (
              <FormField label="Password" required error={touched && form.password.length < 8 ? 'At least 8 characters' : undefined}
                hint="Give it to them privately; they can change it after signing in.">
                <Input type="password" value={form.password} onChange={f('password')} autoComplete="new-password" placeholder="Min. 8 characters" />
              </FormField>
            )}
            <FormField label="Role" required hint={roleLocked ? `${existing?.full_name} is the only active ${existing?.role} — at least one is required.` : undefined}>
              <NativeSelect value={form.role} onChange={f('role')} disabled={roleLocked}>
                {roles.map((r) => {
                  const taken = SINGLETON_ROLES.includes(r) && r !== existing?.role && activeCountOf(r) >= 1;
                  return <option key={r} value={r} disabled={taken}>{r}{taken ? ' (already assigned)' : ''}</option>;
                })}
              </NativeSelect>
            </FormField>
            {form.role === 'Doctor' && (
              <FormField label="Specialty" hint="Used for same-specialty emergency cover.">
                <NativeSelect value={form.specialty_id} onChange={f('specialty_id')}>
                  <option value="">— none —</option>
                  {specialties.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </NativeSelect>
              </FormField>
            )}
          </SheetBody>
          <SheetFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={saving}>{isNew ? 'Add user' : 'Save changes'}</Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function RegistrationModal({ user, onClose, onSaved }: { user: AdminUser; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState(() => ({
    registration_no: user.registration_no || '',
    registration_council: user.registration_council || '',
    qualification: user.qualification || '',
    designation: user.designation || '',
    hpr_id: user.hpr_id || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const f = (k: keyof typeof form, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await updateUser(user.id, {
        registration_no: form.registration_no.trim() || null,
        registration_council: form.registration_council.trim() || null,
        qualification: form.qualification.trim() || null,
        designation: form.designation.trim() || null,
        hpr_id: form.hpr_id.trim() || null,
      });
      onSaved();
      onClose();
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Registration — {user.full_name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit}>
          {error && <div className="mb-3 rounded-[10px] border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">{error}</div>}
          <div className="mb-3 text-[13px] text-muted-foreground">
            Printed beside every e-signature on a generated case sheet. Required before {user.full_name} can sign an IP assessment or progress note.
          </div>
          <div className="mb-3">
            <label className="lbl">Registration number *</label>
            <input value={form.registration_no} onChange={(e) => f('registration_no', e.target.value)} placeholder="e.g. TNMC 12345 / TNNC 98765" autoFocus />
          </div>
          <div className="grid2 mb-3">
            <div><label className="lbl">Council</label><input value={form.registration_council} onChange={(e) => f('registration_council', e.target.value)} placeholder="e.g. TNMC, TN Nursing Council" /></div>
            <div><label className="lbl">Qualification</label><input value={form.qualification} onChange={(e) => f('qualification', e.target.value)} placeholder="e.g. MD (Gen Med)" /></div>
          </div>
          <div className="grid2 mb-4">
            <div><label className="lbl">Designation</label><input value={form.designation} onChange={(e) => f('designation', e.target.value)} placeholder="e.g. Consultant Physician" /></div>
            <div><label className="lbl">HPR ID</label><input value={form.hpr_id} onChange={(e) => f('hpr_id', e.target.value)} placeholder="ABDM Health Professional Registry id" /></div>
          </div>
          <div className="flex justify-end gap-2.5">
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BranchesModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const [data, setData] = useState<{ granted: UserBranchGrant[]; available: UserBranchGrant[] } | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [defaultId, setDefaultId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getUserBranches(user.id).then(({ granted, available }) => {
      setData({ granted, available });
      setSelected(granted.map((b) => b.id));
      setDefaultId(granted.find((b) => b.is_default)?.id || granted[0]?.id || available[0]?.id || null);
    }).catch((e) => setError(e.message));
  }, [user.id]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      if (defaultId !== null && !next.includes(defaultId)) setDefaultId(next[0] || null);
      return next;
    });
  };

  const submit = async () => {
    if (!selected.length || !defaultId) return;
    setSaving(true); setError('');
    try {
      await setUserBranches(user.id, { branch_ids: selected, default_branch_id: defaultId });
      onClose();
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Branch access — {user.full_name}</DialogTitle>
        </DialogHeader>
        {error && <div style={{ padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}
        {!data ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#6B7280' }}>Loading…</div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: '#6B7280' }}>
              Which branches {user.full_name} can act in, and which one they land on by default.
            </div>
            {data.available.map((b) => (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', borderBottom: '1px solid #F3F4F6' }}>
                <Checkbox checked={selected.includes(b.id)} onCheckedChange={() => toggle(b.id)} id={`br-${b.id}`} />
                <label htmlFor={`br-${b.id}`} style={{ flex: 1, fontSize: 13, cursor: 'pointer' }}>{b.name} <span style={{ color: '#9CA3AF', fontFamily: 'monospace', fontSize: 11 }}>{b.code}</span></label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6B7280', cursor: selected.includes(b.id) ? 'pointer' : 'not-allowed' }}>
                  <input type="radio" name="defaultBranch" checked={defaultId === b.id} disabled={!selected.includes(b.id)} onChange={() => setDefaultId(b.id)} />
                  Default
                </label>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button onClick={submit} disabled={saving || !selected.length}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChangePasswordModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setSaving(true); setError('');
    try {
      await updateUser(user.id, { password });
      setDone(true);
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Change password — {user.full_name}</DialogTitle>
        </DialogHeader>
        {done ? (
          <>
            <div style={{ padding: '10px 12px', background: '#ECFDF5', border: '1px solid #6EE7B7', borderRadius: 'var(--radius)', color: '#065F46', fontSize: 13 }}>
              ✅ Password updated for {user.username}.
            </div>
            <Button onClick={onClose}>Close</Button>
          </>
        ) : (
          <form onSubmit={submit}>
            {error && <div style={{ marginBottom: 12, padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}
            <div style={{ marginBottom: 12 }}>
              <label className="lbl">New password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 8 characters" autoFocus />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="lbl">Confirm password</label>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Update password'}</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const MODULES = [
  { id: 'dashboard', label: 'Dashboard' },
  // G2: the ClintoBytes Copilot chatbot. It only ever shows what this role's own screens show.
  { id: 'copilot', label: 'ClintoBytes Copilot (chat assistant)' },
  { id: 'clinical-references', label: 'Hospital references (ask the approved clinical references)' },
  { id: 'clinical-references:web', label: 'Web search references (search trusted medical websites)' },
  { id: 'clinical-library:approve', label: 'Reference Library — approve or send back documents' },
  { id: 'voice', label: 'Intake' },
  { id: 'visit', label: 'Visit workflow' },
  { id: 'queue', label: 'Queue' },
  // Changing a patient's clinical priority (Urgent / Emergency) or the queue
  // preferences that move them (referral, travel distance). Seeded to every
  // role that held 'queue' or 'visit' (migration_queue_prioritization.sql),
  // since all of them could already do this; every change is audited.
  { id: 'queue:priority', label: 'Queue — change patient priority & queue preferences' },
  { id: 'appointments', label: 'Appointments' },
  { id: 'consultation', label: 'Consultation' },
  { id: 'records', label: 'Patient records' },
  // The patient-level legal audit trail (who changed or viewed a record).
  // Separate from 'records' because who-looked-at-whom is itself sensitive —
  // Admin by default (migration_clinical_audit_vitals.sql).
  { id: 'records:audit', label: 'Patient records — legal audit trail' },
  // Publishing a document into the patient app's own record — a materially
  // different power from reading/uploading one in the Documents tab, same
  // separation as the Laboratory module's lab:release. Admin by default
  // (migration_patient_document_release.sql); a hospital grants it to Doctor/
  // Nurse explicitly if it wants release to happen without an Admin.
  { id: 'records:release-to-patient', label: 'Patient records — release documents to patient app' },
  // The Emergency / Casualty board. Like 'laboratory', it grants READ of the
  // whole ED screen; what a holder can DO comes from the er:* capabilities.
  { id: 'emergency', label: 'Emergency / Casualty' },
  { id: 'ip', label: 'IP / Wards' },
  // Correcting an already-saved nursing vital. Never an edit — it creates an
  // audited new version with a mandatory reason. Seeded to Nurse and Admin.
  { id: 'ip:vitals-amend', label: 'IP — amend saved nursing vitals' },
  // The LIS. Grants READ of the whole Laboratory screen — every work queue,
  // every order, every result. What a holder can DO inside it comes from the
  // lab:* capabilities below, which is why this one is safe to give broadly
  // (a receptionist telling a patient where their blood test is needs it).
  { id: 'laboratory', label: 'Laboratory' },
  { id: 'pharmacy', label: 'Pharmacy' },
  // A separate grant from Pharmacy above, not a sub-setting of it. The
  // walk-in counter takes money, applies discounts and issues refunds, while
  // 'pharmacy' is held by Doctor and Nurse roles for Dispensing and
  // Inventory — folding the two together would hand every doctor a cash till.
  { id: 'pharmacy:outpatient', label: 'Pharmacy — walk-in counter' },
  // Medicine Borrow & Return: lending stock to another hospital and
  // collecting it back or its value. Separate for the same reason — it moves
  // stock out of the building and takes money in.
  { id: 'pharmacy:borrow', label: 'Pharmacy — borrow & return' },
  // Sets a whole branch's stock counts from an Excel workbook (legacy
  // migration). Master-data rows in it still need admin:drug-master /
  // admin:formulary, checked per row on the server.
  { id: 'pharmacy:inventory-upload', label: 'Pharmacy — inventory upload (Excel)' },
  // G15: approves manual stock adjustments over the hospital's limit — never their own.
  { id: 'pharmacy:approve-adjustment', label: 'Pharmacy — approve stock adjustments' },
  { id: 'billing', label: 'Billing' },
  // OPD fee schedule: billing a different fee/GST, a discount over an item's
  // limit, or a charge not on the schedule (utils/opdFeeSchedule.js).
  { id: 'billing:fee-override', label: 'Billing — change OPD fees, extra discounts & unscheduled charges' },
  { id: 'reports', label: 'Reports' },
  { id: 'admin-panel', label: 'Admin Panel' },
];

// The Laboratory capabilities. Deliberately separate grants rather than one
// "laboratory: write" — the separations are the module's whole safety story:
//
//   * result entry vs verification — a number on a screen is not a result
//     until someone qualified has taken responsibility for it. Granting both
//     to one role is a hospital's decision to make explicitly.
//   * verification vs release — one is a clinical judgement about the numbers,
//     the other publishes them to the patient's record.
//   * release vs amend — reissuing a report a patient is already holding is
//     rarer and more consequential than issuing the first one.
//
// Every one is re-checked server-side on each request (routes/laboratory/*);
// unchecking a box here removes a real capability, not just a button.
const LAB_SUBMODULES = [
  { id: 'lab:orders', label: 'Order tests, edit & cancel orders' },
  // Replacing a test's configured price/GST on an order (which then flows to
  // its invoice or admission charge). Admin only by default.
  { id: 'lab:price-override', label: 'Override laboratory prices on an order' },
  { id: 'lab:collect', label: 'Collect samples & print barcodes' },
  { id: 'lab:receive', label: 'Receive, accept & reject samples' },
  // Dispatch to a reference laboratory, track it, attach/replace the report
  // that comes back, and run OCR on it. Transcribing, verifying and finalizing
  // that report stay on result-entry / verify / release below.
  { id: 'lab:outsource', label: 'Send out samples & attach outside lab reports' },
  { id: 'lab:result-entry', label: 'Enter results (incl. outside lab results)' },
  { id: 'lab:verify', label: 'Verify results (sign off)' },
  { id: 'lab:release', label: 'Release reports & finalize outside lab results' },
  { id: 'lab:amend', label: 'Amend released reports & correct finalized results' },
  { id: 'lab:critical', label: 'Acknowledge critical results' },
];

// The Emergency capabilities (migration_emergency.sql). Separate grants for the
// same reason as the IP ones: ordering vs administering a medication are two
// people's jobs (Doctor lacks er:med-admin, Nurse lacks er:med-order by
// default), and the MLC register is restricted to whoever is granted er:mlc.
// Death / brought-in-dead additionally require the Doctor or Admin ROLE.
const ER_SUBMODULES = [
  { id: 'er:register', label: 'Register arrivals & resolve identity' },
  { id: 'er:triage', label: 'Triage & re-triage' },
  { id: 'er:clinical', label: 'Notes, procedures, consults, bay & attending' },
  { id: 'er:med-order', label: 'Order medications & countersign verbal orders' },
  { id: 'er:med-admin', label: 'Chart medication given (incl. verbal resus orders)' },
  { id: 'er:mlc', label: 'MLC register & police intimation (restricted)' },
  { id: 'er:disposition', label: 'Disposition, admission & departure' },
  { id: 'er:documents', label: 'Approve ED documents (discharge summary, referral letter…)' },
  { id: 'er:charges', label: 'Add / remove ED charges' },
];

// The capability chips under a ticked module. Tailwind rather than the inline
// styles the older blocks in this file use: features/** forbids new inline
// style={{}} (eslint.config.mjs), so new blocks use this instead.
function CapabilityChips({ title, options, selected, onToggle, compact = false }: {
  title: string; options: Array<{ id: string; label: string }>; selected: string[]; onToggle: (id: string) => void; compact?: boolean;
}) {
  return (
    <div className={compact ? 'mt-2 border-l-2 border-border pl-3' : 'mt-2.5 border-l-2 border-border pl-3.5'}>
      <div className="mb-1.5 text-[11px] text-ink-muted">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((m) => (
          <label key={m.id} className={`flex cursor-pointer items-center gap-1 rounded-2xl border px-2 py-0.5 ${compact ? 'text-[11px]' : 'text-[11.5px]'} ${selected.includes(m.id) ? 'border-teal bg-accent' : 'border-border bg-muted'}`}>
            <Checkbox checked={selected.includes(m.id)} onCheckedChange={() => onToggle(m.id)} />
            {m.label}
          </label>
        ))}
      </div>
    </div>
  );
}

const ADMIN_SUBMODULES = [
  { id: 'admin:users', label: 'Users & Roles' },
  { id: 'admin:ailments', label: 'Ailment Templates (create/edit)' },
  { id: 'admin:ai-role', label: 'AI Assistant Role (create/edit)' },
  { id: 'admin:merges', label: 'Patient Merges' },
  { id: 'admin:wards', label: 'Wards & Beds' },
  { id: 'admin:specialties', label: 'Specialties' },
  { id: 'admin:availability', label: 'Doctor Availability' },
  { id: 'admin:storage', label: 'Storage' },
  { id: 'admin:intake', label: 'Intake Form' },
  { id: 'admin:ai-settings', label: 'AI Settings' },
  { id: 'admin:ai-activity', label: 'AI Activity (agents, failed AI tasks)' },
  { id: 'admin:clinical-library', label: 'Reference Library (add, retire and re-index clinical references)' },
  // Rewriting the text patients are asked to accept, and how long a consent
  // stays valid — its own grant rather than folded into another settings tab,
  // for the same reason the SMS credential is: it changes what the hospital is
  // legally on record as having asked, not just how a screen looks.
  { id: 'admin:consent', label: 'Consent Forms (wording & expiry)' },
  { id: 'admin:branding', label: 'Branding' },
  { id: 'admin:branches', label: 'Branches' },
  { id: 'admin:billing-settings', label: 'Billing Settings' },
  // Holds the MSG91 credential and the switch that decides whether messages
  // actually reach patients — a materially different power from the other
  // settings tabs, which is why it is its own grant rather than folded into
  // one of them.
  { id: 'admin:sms-gateway', label: 'SMS Gateway (credentials & sending)' },
  // Separate from the counter grant above: whoever runs the till should not
  // also be able to rewrite the earning rules or grant themselves points.
  { id: 'admin:pharmacy-loyalty', label: 'Pharmacy admin (loyalty points & adjustments)' },
  // Editing a reference range changes what "normal" means for every future
  // patient at this hospital. That belongs with the other master-data grants,
  // never with bench work — which is why it is here and not a lab:* capability.
  { id: 'admin:laboratory', label: 'Laboratory masters (tests, parameters & reference ranges)' },
  // Triage scale, observation/boarding limits, bays, MLC jurisdiction rules,
  // payer schemes — Admin Panel → Emergency Dept Customizations.
  { id: 'admin:emergency', label: 'Emergency Dept Customizations' },
  // Facility → Queue Prioritization: how routine OPD patients are ordered.
  { id: 'admin:queue-priority', label: 'Queue Prioritization' },
  // Clinical → Vitals Form Customization: the symptom fields asked at Vitals.
  { id: 'admin:form-customization', label: 'Vitals Form Customization (symptom fields)' },
];

function RolesTab() {
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftModules, setDraftModules] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newModules, setNewModules] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [mfaConfirm, setMfaConfirm] = useState<Role | null>(null);

  // Two-step login policy (G20). Turning it on signs nobody out; each user of
  // the role is asked to set it up at their next sign-in.
  const setRoleMfa = async (role: Role, on: boolean) => {
    setError('');
    try {
      await updateRole(role.id, { mfa_required: on });
      toast({ title: on ? `${role.name}: two-step login required` : `${role.name}: two-step login optional`, variant: 'success' });
      await load();
    } catch (e: any) { setError(e.message); }
  };

  const load = () => getAllRoles().then(setRoles).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const startEdit = (role: Role) => { setEditingId(role.id); setDraftModules(role.modules || []); setError(''); };
  const cancelEdit = () => { setEditingId(null); setDraftModules([]); };
  const toggleDraftModule = (id: string) => setDraftModules((p) => (p.includes(id) ? p.filter((m) => m !== id) : [...p, id]));

  const saveEdit = async () => {
    if (editingId === null) return;
    setSaving(true); setError('');
    try {
      await updateRole(editingId, { modules: draftModules });
      setEditingId(null);
      await load();
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  const toggleNewModule = (id: string) => setNewModules((p) => (p.includes(id) ? p.filter((m) => m !== id) : [...p, id]));

  const submitNew = async (e: FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) { setError('Role name is required'); return; }
    setCreating(true); setError('');
    try {
      await createRole({ name: newName.trim(), modules: newModules });
      setNewName(''); setNewModules([]); setShowNewForm(false);
      await load();
    } catch (e: any) { setError(e.message); }
    setCreating(false);
  };

  const remove = async (role: Role) => {
    setError('');
    try { await deleteRole(role.id); await load(); } catch (e: any) { setError(e.message); }
  };

  if (!roles) return <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading roles…</div>;

  return (
    <div>
      {error && <div style={{ marginBottom: 14, padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}

      <div style={{ marginBottom: 16 }}>
        <button className="btn-primary" onClick={() => setShowNewForm((s) => !s)}>{showNewForm ? '✕ Cancel' : '+ New role'}</button>
      </div>

      {showNewForm && (
        <form onSubmit={submitNew} className="card" style={{ maxWidth: 560, marginBottom: 20, padding: 16 }}>
          <div style={{ marginBottom: 14 }}>
            <label className="lbl">Role name *</label>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Lab Technician" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label className="lbl">Modules this role can access</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
              {MODULES.map((m) => (
                <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: newModules.includes(m.id) ? 'rgba(11,95,214,.08)' : '#F9FAFB', border: `1px solid ${newModules.includes(m.id) ? 'var(--teal)' : '#E5E7EB'}`, borderRadius: 20, fontSize: 12.5, cursor: 'pointer' }}>
                  <Checkbox checked={newModules.includes(m.id)} onCheckedChange={() => toggleNewModule(m.id)} />
                  {m.label}
                </label>
              ))}
            </div>
            {newModules.includes('laboratory') && (
              <div style={{ marginTop: 10, paddingLeft: 14, borderLeft: '2px solid #E5E7EB' }}>
                <div style={{ fontSize: 11.5, color: '#6B7280', marginBottom: 6 }}>What this role can do in the Laboratory:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {LAB_SUBMODULES.map((m) => (
                    <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', background: newModules.includes(m.id) ? 'rgba(11,95,214,.08)' : '#F9FAFB', border: `1px solid ${newModules.includes(m.id) ? 'var(--teal)' : '#E5E7EB'}`, borderRadius: 16, fontSize: 11.5, cursor: 'pointer' }}>
                      <Checkbox checked={newModules.includes(m.id)} onCheckedChange={() => toggleNewModule(m.id)} />
                      {m.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {newModules.includes('emergency') && (
              <CapabilityChips title="What this role can do in Emergency:" options={ER_SUBMODULES} selected={newModules} onToggle={toggleNewModule} />
            )}
            {newModules.includes('admin-panel') && (
              <div style={{ marginTop: 10, paddingLeft: 14, borderLeft: '2px solid #E5E7EB' }}>
                <div style={{ fontSize: 11.5, color: '#6B7280', marginBottom: 6 }}>Which Admin Panel tabs:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {ADMIN_SUBMODULES.map((m) => (
                    <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', background: newModules.includes(m.id) ? 'rgba(11,95,214,.08)' : '#F9FAFB', border: `1px solid ${newModules.includes(m.id) ? 'var(--teal)' : '#E5E7EB'}`, borderRadius: 16, fontSize: 11.5, cursor: 'pointer' }}>
                      <Checkbox checked={newModules.includes(m.id)} onCheckedChange={() => toggleNewModule(m.id)} />
                      {m.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button type="submit" className="btn-primary" disabled={creating}>{creating ? 'Creating…' : 'Create role'}</button>
        </form>
      )}

      <Table>
        <TableHeader>
          <TableRow>{['Role', 'Modules', 'Active users', 'Two-step login', '', ''].map((h, i) => <TableHead key={i}>{h}</TableHead>)}</TableRow>
        </TableHeader>
        <TableBody>
          {roles.map((r) => (
            <TableRow key={r.id}>
              <TableCell style={{ fontWeight: 600 }}>
                {r.name}
                {r.is_system && <span className="badge badge-gray" style={{ marginLeft: 8, fontSize: 10, background: '#F3F4F6', color: '#6B7280' }}>built-in</span>}
              </TableCell>
              <TableCell style={{ maxWidth: 340 }}>
                  {editingId === r.id ? (
                    <div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {MODULES.map((m) => (
                          <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 8px', background: draftModules.includes(m.id) ? 'rgba(11,95,214,.08)' : '#F9FAFB', border: `1px solid ${draftModules.includes(m.id) ? 'var(--teal)' : '#E5E7EB'}`, borderRadius: 16, fontSize: 11.5, cursor: 'pointer' }}>
                            <Checkbox checked={draftModules.includes(m.id)} onCheckedChange={() => toggleDraftModule(m.id)} />
                            {m.label}
                          </label>
                        ))}
                      </div>
                      {draftModules.includes('laboratory') && (
                        <div style={{ marginTop: 8, paddingLeft: 12, borderLeft: '2px solid #E5E7EB' }}>
                          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 5 }}>What this role can do in the Laboratory:</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                            {LAB_SUBMODULES.map((m) => (
                              <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 7px', background: draftModules.includes(m.id) ? 'rgba(11,95,214,.08)' : '#F9FAFB', border: `1px solid ${draftModules.includes(m.id) ? 'var(--teal)' : '#E5E7EB'}`, borderRadius: 14, fontSize: 11, cursor: 'pointer' }}>
                                <Checkbox checked={draftModules.includes(m.id)} onCheckedChange={() => toggleDraftModule(m.id)} />
                                {m.label}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      {draftModules.includes('emergency') && (
                        <CapabilityChips title="What this role can do in Emergency:" options={ER_SUBMODULES} selected={draftModules} onToggle={toggleDraftModule} compact />
                      )}
                      {draftModules.includes('admin-panel') && (
                        <div style={{ marginTop: 8, paddingLeft: 12, borderLeft: '2px solid #E5E7EB' }}>
                          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 5 }}>Which Admin Panel tabs:</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                            {ADMIN_SUBMODULES.map((m) => (
                              <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 7px', background: draftModules.includes(m.id) ? 'rgba(11,95,214,.08)' : '#F9FAFB', border: `1px solid ${draftModules.includes(m.id) ? 'var(--teal)' : '#E5E7EB'}`, borderRadius: 14, fontSize: 11, cursor: 'pointer' }}>
                                <Checkbox checked={draftModules.includes(m.id)} onCheckedChange={() => toggleDraftModule(m.id)} />
                                {m.label}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#6B7280' }}>
                      {(r.modules || []).length === 0 ? <span style={{ color: '#DC2626' }}>No modules granted</span> : r.modules.map((id) => [...MODULES, ...LAB_SUBMODULES, ...ER_SUBMODULES, ...ADMIN_SUBMODULES].find((m) => m.id === id)?.label || id).join(', ')}
                    </div>
                  )}
              </TableCell>
              <TableCell style={{ fontSize: 12, color: '#6B7280' }}>{r.active_user_count}</TableCell>
              <TableCell>
                <label className="flex items-center gap-2 text-xs text-ink-muted">
                  <Switch checked={!!r.mfa_required} aria-label={`Require two-step login for ${r.name}`}
                    onCheckedChange={(on) => (on ? setMfaConfirm(r) : setRoleMfa(r, false))} />
                  {r.mfa_required ? 'Required' : 'Optional'}
                </label>
              </TableCell>
              <TableCell>
                {editingId === r.id ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button size="sm" disabled={saving} onClick={saveEdit}>{saving ? '…' : 'Save'}</Button>
                    <Button variant="ghost" size="sm" disabled={saving} onClick={cancelEdit}>Cancel</Button>
                  </div>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => startEdit(r)}>✏ Edit modules</Button>
                )}
              </TableCell>
              <TableCell>
                {!r.is_system && (
                  <Button variant="danger" size="sm"
                    disabled={r.active_user_count > 0} title={r.active_user_count > 0 ? 'Reassign users off this role first' : ''}
                    onClick={() => remove(r)}>
                    Delete
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ConfirmDialog
        open={!!mfaConfirm}
        onOpenChange={(o) => { if (!o) setMfaConfirm(null); }}
        title={`Require two-step login for ${mfaConfirm?.name ?? ''}?`}
        description="Nobody is signed out now. At their next sign-in, each user with this role who hasn't set up two-step login will be asked to scan a QR code with an authenticator app on their phone. Make sure they have one before you switch this on."
        confirmLabel="Require two-step login"
        onConfirm={() => { const r = mfaConfirm; setMfaConfirm(null); if (r) setRoleMfa(r, true); }}
      />
    </div>
  );
}
